import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Square } from "lucide-react";
import { uploadToCloudinary, isCloudinaryConfigured, compressImage } from "../../cloudinary/upload";
import { usePremium } from "../../firebase/billing";
import { checkAttachment, limitsFor } from "./attachmentLimits";
import { requestAlarmPermission } from "../../capacitor/alarmPermissions";
import { Capacitor } from "@capacitor/core";

// One place for the "pick or capture a photo / video / audio clip, or record a
// voice note, then upload it to Cloudinary" flow. The Planner's attachment
// field, the Planner notes toolbar and the Journal's Insert panel all use it,
// so they can't drift apart. `onUploaded(att)` gets the same record
// uploadToCloudinary resolves with ({ id, url, kind, ... }).
//
// `count` is how many attachments the item already holds, to enforce the
// per-item cap (see attachmentLimits.js). Leave it out where there is no
// per-item list (the Journal's inline media): only the file-size cap applies.
//
// `inputs` is a set of hidden file inputs the caller must render ALWAYS-MOUNTED
// (not inside a panel that closes on tap): on mobile the native picker covers
// the app, and an input that unmounts meanwhile never fires onChange.
export function useMediaUpload({ onUploaded, count }) {
  // While the plan is still loading nobody is held to the free limit, so a
  // Premium user's first upload isn't wrongly refused (same as ReminderPicker).
  const { isPremium, loading: premiumLoading } = usePremium();
  const premium = isPremium || premiumLoading;
  const limits = limitsFor(premium);
  const atLimit = count != null && count >= limits.maxCount;
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [recording, setRecording] = useState(false);
  // True when the last Record attempt was refused by the OS. The error pill
  // then offers a button straight to Clarity's page in Android Settings.
  const [micDenied, setMicDenied] = useState(false);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const cameraRef = useRef(null);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const doUpload = async (file) => {
    if (!file) return;
    setError("");
    const countErr = count != null ? checkAttachment({ count, premium }) : "";
    if (countErr) { setError(countErr); return; }
    setUploading(true); setProgress(0);
    try {
      // Shrink photos before sending so uploads finish much faster.
      const toSend = await compressImage(file);
      // Checked after compressing: a big phone photo shrinks well under the cap.
      const sizeErr = checkAttachment({ bytes: toSend.size, premium });
      if (sizeErr) { setError(sizeErr); return; }
      const att = await uploadToCloudinary(toSend, setProgress);
      onUploadedRef.current?.(att);
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; doUpload(f); };

  const startRec = async () => {
    setError("");
    setMicDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        doUpload(new File([blob], `recording-${Date.now()}.${ext}`, { type }));
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setError("Microphone access denied");
      setMicDenied(true);
    }
  };
  const openMicSettings = async () => {
    const msg = await requestAlarmPermission("appSettings");
    if (msg) setError(msg);
    else { setError(""); setMicDenied(false); }
  };
  const stopRec = () => { recorderRef.current?.stop(); setRecording(false); };

  const inputs = (
    <>
      <input ref={photoRef} type="file" accept="image/*,video/*" style={{ display: "none" }} onChange={pick} />
      <input ref={videoRef} type="file" accept="video/*" style={{ display: "none" }} onChange={pick} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={pick} />
      <input ref={audioRef} type="file" accept="audio/*" style={{ display: "none" }} onChange={pick} />
    </>
  );

  return {
    configured: isCloudinaryConfigured,
    uploading, progress, error, setError, recording, atLimit, limits,
    micDenied: micDenied && Capacitor.isNativePlatform(), openMicSettings,
    inputs,
    openPhoto: () => photoRef.current?.click(),
    openVideo: () => videoRef.current?.click(),
    openCamera: () => cameraRef.current?.click(),
    openAudio: () => audioRef.current?.click(),
    startRec, stopRec,
  };
}

// Recording / uploading / error feedback for a useMediaUpload(). `floating`
// pins it to the top of the screen — for callers whose buttons live in a panel
// that closes on tap (the Journal's Insert panel), so Stop is still reachable.
export function MediaStatus({ media, floating = false }) {
  const { uploading, progress, error, recording, stopRec, micDenied, openMicSettings } = media;
  if (!uploading && !error && !recording) return null;
  const pill = {
    display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", borderRadius: 999,
    fontSize: 12.5, fontWeight: 700, fontFamily: "inherit", background: "var(--c-bg-card)", color: "var(--c-text)",
    border: "1.5px solid var(--c-border)", boxShadow: "0 4px 14px rgba(0,0,0,0.14)",
  };
  const body = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: floating ? "center" : "flex-start", gap: 6 }}>
      {recording && (
        <span style={pill}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#e0486a" }} />
          Recording…
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={stopRec}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, minHeight: 0, minWidth: 0, padding: "4px 10px", borderRadius: 999, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 800, color: "#fff", background: "linear-gradient(135deg,#e0608a,#d4789a)" }}>
            <Square size={11} fill="currentColor" /> Stop
          </button>
        </span>
      )}
      {uploading && <span style={pill}>Uploading… {progress}%</span>}
      {error && (
        <span style={{ ...pill, color: "#e0608a" }}>
          {error}
          {micDenied && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={openMicSettings}
              style={{ minHeight: 0, minWidth: 0, padding: "4px 10px", borderRadius: 999, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 800, color: "#fff", background: "linear-gradient(135deg,#e0608a,#d4789a)" }}>
              Open settings
            </button>
          )}
        </span>
      )}
    </div>
  );
  if (!floating) return <div style={{ padding: "6px 10px" }}>{body}</div>;
  return createPortal(
    <div style={{ position: "fixed", top: "calc(var(--sat, 0px) + 12px)", left: 0, right: 0, zIndex: 9700, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
      <div style={{ pointerEvents: "auto" }}>{body}</div>
    </div>,
    document.body
  );
}

// HTML for a freshly uploaded attachment, to drop into a contentEditable note.
// Wrapped contenteditable=false so the player can be tapped; the trailing
// paragraph keeps a line to keep typing on.
export function mediaToHtml(att) {
  const url = String(att?.url || "").replace(/"/g, "&quot;");
  if (!url) return "";
  let el;
  if (att.kind === "image") el = `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:10px;display:block">`;
  else if (att.kind === "video") el = `<video src="${url}" controls playsinline preload="metadata" style="max-width:100%;border-radius:10px;display:block;background:#000"></video>`;
  else if (att.kind === "audio") el = `<audio src="${url}" controls preload="metadata" style="width:100%;display:block"></audio>`;
  else el = `<a href="${url}" target="_blank" rel="noopener noreferrer">${(att.name || "attachment").replace(/[<>&"]/g, "")}</a>`;
  return `<div class="rn-media" contenteditable="false" style="margin:6px 0">${el}</div><p><br></p>`;
}
